import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { primaryButtonStyle, secondaryButtonStyle } from '../shared/styles/buttons.styles';
import { formFieldStyles } from '../shared/styles/formField.styles';
import { redesignTheme } from '../theme';

const { spacing, sizes, colors, fonts, fontSize, breakpoints, radius } = redesignTheme;

export const useAuthStyles = makeStyles((theme: Theme) => ({
  headingGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.xs,
    textAlign: 'center',
    maxWidth: sizes.headingMaxWidth,
  },
  title: {
    margin: 0,
    fontFamily: fonts.gothicSemiBold,
    fontSize: fontSize.lg,
    color: colors.primary,
    lineHeight: 'normal',
    [`@media (min-width: ${breakpoints.desktop}px)`]: {
      fontSize: fontSize.xl,
    },
  },
  subtitle: {
    margin: 0,
    fontFamily: fonts.opensans,
    fontSize: fontSize.base,
    color: colors.textBody,
    lineHeight: 'normal',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: spacing.md,
  },
  fieldsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },
  field: {
    ...formFieldStyles.field,
  },
  label: {
    ...formFieldStyles.label,
  },
  input: {
    ...formFieldStyles.input,
  },
  inputWrapper: {
    ...formFieldStyles.inputWrapper,
  },
  inputWithIcon: {
    ...formFieldStyles.inputWithIcon,
  },
  inputIcon: {
    ...formFieldStyles.inputIcon,
  },
  errorText: {
    color: theme.palette.warning.main,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.xs,
    margin: 0,
  },
  errorTextMultiline: {
    whiteSpace: 'pre-line',
  },
  errorTextMd: {
    color: theme.palette.warning.main,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
    textAlign: 'center',
    margin: 0,
  },
  primaryButton: {
    ...primaryButtonStyle,
    width: '100%',
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colors.primaryDark,
      opacity: 0.92,
      boxShadow: 'none',
    },
    '&.Mui-disabled, &:disabled': {
      color: 'rgba(255,255,255,0.7)',
      backgroundColor: colors.primaryDark,
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  outlineButton: {
    ...secondaryButtonStyle,
    width: '100%',
    height: sizes.buttonHeight,
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colors.hoverSurface,
      boxShadow: 'none',
    },
  },
  outlineButtonTextPrimary: {
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.primary,
    letterSpacing: '0.28px',
  },
  languageSelector: {
    display: 'flex',
    justifyContent: 'center',
  },
  languageSelectorButton: {
    height: 40,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    gap: 10,
    padding: '12px',
  },
  linkPrimary: {
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.primary,
    textDecoration: 'underline',
    lineHeight: 1.4,
  },
  ssoButtonText: {
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.primaryDark,
    letterSpacing: '0.28px',
    paddingLeft: spacing.xs,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    width: '100%',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textMuted,
    lineHeight: 1.5,
  },
  ssoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap' as const,
  },
  footerText: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textMuted,
    lineHeight: 1.5,
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.text,
    '&:hover': {
      color: colors.primary,
    },
  },
  backIcon: {
    fontSize: 16,
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    gap: spacing.md,
  },
  checkCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.md,
    backgroundColor: colors.accentMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statusTitle: {
    fontFamily: fonts.gothicSemiBold,
    fontSize: fontSize.xl,
    color: colors.primary,
    margin: 0,
  },
  statusDescription: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.base,
    lineHeight: 1.6,
    color: colors.textBody,
    margin: 0,
    maxWidth: 400,
  },
  checkIcon: {
    fontSize: 32,
    color: colors.primaryDark,
  },
  semibold: {
    fontFamily: fonts.opensansSemiBold,
  },
}));
